// BarChart.jsx
import React, { useState, useEffect } from 'react';
import { BarChartComponent } from './chart';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ApiURL from '../Api/Api';
import { motion } from 'framer-motion';
import { ArrowPathIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

const BarChart = () => {
  const [transactionData, setTransactionData] = useState([]);
  const [activeDataset, setActiveDataset] = useState('both');
  const [maxAmount, setMaxAmount] = useState(100);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBarChartData();
  }, [activeDataset]);

  const fetchBarChartData = async () => {
    setLoading(true);
    setError(null);
    try {
      const user_id = localStorage.getItem('id');
      const user_name = localStorage.getItem('user_name');
      if (!user_id || !user_name) throw new Error('User not found');

      const response = await fetch(
        `${ApiURL}/Barchart.php?user_id=${user_id}&user_name=${user_name}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.error) throw new Error(data.error);

      setTransactionData(data);

      let max;
      if (activeDataset === 'both') {
        max = Math.max(...data.flatMap(item => [Number(item.credit) || 0, Number(item.debit) || 0]));
      } else {
        max = Math.max(...data.map(item => Number(activeDataset === 'credit' ? item.credit : item.debit) || 0));
      }
      setMaxAmount(max * 1.35 || 100);
    } catch (err) {
      console.error(err);
      setError(err.message);
      setTransactionData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
  };

  const formatCurrency = (amount) =>
    amount != null ? `₹${Number(amount).toLocaleString('en-IN')}` : '₹0';

  /* totals for the mini stat bar */
  const totalCredit = transactionData.reduce((s, d) => s + (Number(d.credit) || 0), 0);
  const totalDebit  = transactionData.reduce((s, d) => s + (Number(d.debit)  || 0), 0);

  const chartData = {
    labels: transactionData.map(item => formatDate(item.date)),
    datasets: activeDataset === 'both'
      ? [
          {
            label: 'Credit',
            data: transactionData.map(item => Number(item.credit) || 0),
            backgroundColor: 'rgba(16,185,129,0.85)',
            hoverBackgroundColor: '#059669',
            borderRadius: { topLeft: 8, topRight: 8 },
            borderSkipped: false,
            barThickness: 28,
            categoryPercentage: 0.7,
            barPercentage: 0.85,
          },
          {
            label: 'Debit',
            data: transactionData.map(item => Number(item.debit) || 0),
            backgroundColor: 'rgba(244,63,94,0.85)',
            hoverBackgroundColor: '#e11d48',
            borderRadius: { topLeft: 8, topRight: 8 },
            borderSkipped: false,
            barThickness: 28,
            categoryPercentage: 0.7,
            barPercentage: 0.85,
          },
        ]
      : [
          {
            label: activeDataset === 'credit' ? 'Credit' : 'Debit',
            data: transactionData.map(item =>
              Number(activeDataset === 'credit' ? item.credit : item.debit) || 0
            ),
            backgroundColor: activeDataset === 'credit'
              ? 'rgba(16,185,129,0.85)'
              : 'rgba(244,63,94,0.85)',
            hoverBackgroundColor: activeDataset === 'credit' ? '#059669' : '#e11d48',
            borderRadius: { topLeft: 8, topRight: 8 },
            borderSkipped: false,
            barThickness: 42,
            categoryPercentage: 0.6,
            barPercentage: 0.9,
          },
        ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: activeDataset === 'both',
        position: 'top',
        align: 'end',
        labels: {
          font: { size: 11, family: "'DM Sans', sans-serif", weight: '600' },
          color: '#64748b',
          padding: 16,
          boxWidth: 10,
          boxHeight: 10,
          borderRadius: 3,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(15,23,42,0.92)',
        titleFont: { size: 12, family: "'DM Sans', sans-serif", weight: '600' },
        bodyFont: { size: 11, family: "'DM Sans', sans-serif" },
        padding: 12,
        cornerRadius: 10,
        callbacks: {
          label: (ctx) => ` ${ctx.dataset.label}: ${formatCurrency(ctx.raw)}`,
        },
      },
      datalabels: {
        display: (ctx) => {
          const v = ctx.dataset.data[ctx.dataIndex];
          return v > 0;
        },
        color: '#475569',
        backgroundColor: 'rgba(255,255,255,0.92)',
        borderRadius: 5,
        padding: { top: 3, bottom: 3, left: 5, right: 5 },
        font: { size: 10, family: "'DM Sans', sans-serif", weight: '600' },
        formatter: (value) => value > 0 ? formatCurrency(value) : '',
        anchor: 'end',
        align: 'top',
        offset: 4,
        clamp: true,
        clip: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: maxAmount,
        border: { display: false },
        ticks: {
          callback: (v) => `₹${Number(v).toLocaleString('en-IN')}`,
          font: { size: 10, family: "'DM Sans', sans-serif" },
          color: '#94a3b8',
          maxTicksLimit: 5,
        },
        grid: {
          color: '#f1f5f9',
          lineWidth: 1,
        },
      },
      x: {
        border: { display: false },
        ticks: {
          font: { size: 10, family: "'DM Sans', sans-serif" },
          color: '#94a3b8',
          maxRotation: 0,
        },
        grid: { display: false },
      },
    },
    animation: { duration: 600, easing: 'easeOutQuart' },
    hover: { mode: 'nearest', intersect: true },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="pro-card overflow-hidden"
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-sm">
            <CalendarDaysIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-800 leading-tight">Transaction Overview</h2>
            <p className="text-[11px] text-slate-400">Last 7 days activity</p>
          </div>
        </div>

        {/* Toggle */}
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
          {[
            { value: 'both',   label: 'Both'   },
            { value: 'credit', label: 'Credit' },
            { value: 'debit',  label: 'Debit'  },
          ].map(btn => (
            <button
              key={btn.value}
              onClick={() => setActiveDataset(btn.value)}
              className={`px-3 py-1 rounded-lg text-[11px] font-semibold transition-all ${
                activeDataset === btn.value
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {btn.label}
            </button>
          ))}
        </div>
      </div>

      {/* Mini stat row */}
      {!loading && !error && transactionData.length > 0 && (
        <div className="flex flex-col gap-2 px-5 pt-4 sm:flex-row sm:items-center sm:gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 flex-shrink-0" />
            <span className="text-[11px] text-slate-500 font-medium">Credit</span>
            <span className="text-[12px] font-bold text-emerald-600">{formatCurrency(totalCredit)}</span>
          </div>
          <div className="hidden h-4 w-px bg-slate-200 sm:block" />
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500 flex-shrink-0" />
            <span className="text-[11px] text-slate-500 font-medium">Debit</span>
            <span className="text-[12px] font-bold text-rose-600">{formatCurrency(totalDebit)}</span>
          </div>
        </div>
      )}

      {/* Chart area */}
      <div className="px-5 pb-5 pt-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center" style={{ height: 280 }}>
            <ArrowPathIcon className="w-6 h-6 text-indigo-400 animate-spin mb-2" />
            <p className="text-sm text-slate-400">Loading chart…</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center" style={{ height: 280 }}>
            <p className="text-sm text-rose-500 mb-2">{error}</p>
            <button onClick={fetchBarChartData} className="text-indigo-600 text-sm font-semibold">Retry</button>
          </div>
        ) : transactionData.length === 0 ? (
          <div className="flex flex-col items-center justify-center" style={{ height: 280 }}>
            <p className="text-sm text-slate-400">No transaction data for this period</p>
          </div>
        ) : (
          <div style={{ height: 280 }}>
            <BarChartComponent data={chartData} options={options} />
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default BarChart;
